import type { EhrInsurancePlan } from "@/src/lib/ehr/ehr-integration";
import { isMeaningfulInsurancePlanName } from "@/src/lib/insurance/match-valerie-insurance-plan";
export { isMeaningfulInsurancePlanName } from "@/src/lib/insurance/match-valerie-insurance-plan";
import type { Coverage } from "@/src/models/coverage";

function normalizeInsuranceIdentity(value: string | null | undefined): string {
  return (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function resolveUniqueKeyMatch(plans: readonly EhrInsurancePlan[], key: string | null): EhrInsurancePlan | null {
  if (!key) return null;

  const matches = plans.filter((plan) => plan.verifiedMatchKeys.includes(key));

  return matches.length === 1 ? matches[0]! : null;
}

export function buildInsurancePlanMatchKey(
  payer: string | null | undefined,
  plan: string | null | undefined
): string | null {
  const normalizedPayer = normalizeInsuranceIdentity(payer);
  const normalizedPlan = normalizeInsuranceIdentity(plan);

  return normalizedPayer && normalizedPlan ? `payer-plan:${normalizedPayer}::${normalizedPlan}` : null;
}

export function resolveEhrInsurancePlan(
  plans: readonly EhrInsurancePlan[],
  coverage: Coverage,
  eligibilityPayerName: string | null,
  eligibilityProductText: string | null,
  eligibilityPlanName: string | null,
  candidatePlanIds: readonly string[] = []
): EhrInsurancePlan | null {
  const planIds = coverage.valerieInsurancePlanId ? [coverage.valerieInsurancePlanId] : candidatePlanIds;

  if (
    planIds.length &&
    (candidatePlanIds.length || plans.some((plan) => plan.affiliatedValerieInsurancePlanIds !== undefined))
  ) {
    const matches = plans.filter((plan) => plan.affiliatedValerieInsurancePlanIds?.some((id) => planIds.includes(id)));
    const [match] = matches;

    // Every candidate must have a confirmed affiliation to the same package.
    return matches.length === 1 && match && planIds.every((id) => match.affiliatedValerieInsurancePlanIds?.includes(id))
      ? match
      : null;
  }

  const eligibilityKey = buildInsurancePlanMatchKey(eligibilityPayerName, eligibilityProductText);

  if (eligibilityKey) {
    const eligibilityMatch = resolveUniqueKeyMatch(plans, eligibilityKey);

    if (eligibilityMatch || isMeaningfulInsurancePlanName(eligibilityPlanName)) return eligibilityMatch;
  }

  // Missing plans may use an explicitly verified payer mapping.
  if (!coverage.plan?.trim() && !eligibilityProductText?.trim() && !eligibilityPlanName?.trim()) {
    if (coverage.order === 1) {
      const payerId = coverage.customerPayer?.trim().toUpperCase();

      return resolveUniqueKeyMatch(plans, payerId ? `primary-payer-id-no-plan:${payerId}` : null);
    }

    if (coverage.order === 2) {
      const payer = normalizeInsuranceIdentity(coverage.payer);

      return resolveUniqueKeyMatch(plans, payer ? `secondary-payer-no-plan:${payer}` : null);
    }
  }

  return resolveUniqueKeyMatch(plans, buildInsurancePlanMatchKey(coverage.payer, coverage.plan));
}
